import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import config from "../../../../config.json";
import {
  NUTRITION_OPTIONS,
  getNutritionOption,
  normalizeNutritionValuesForSave,
} from "../../../constants/nutritionOptions";
import "./NutritionValuesEditor.css";
import "./ProductExtras.css";

function buildDraftFromValues(values) {
  const map = new Map();
  normalizeNutritionValuesForSave(values).forEach((item) => {
    map.set(item.type, item.value || "");
  });
  return map;
}

export default function NutritionValuesEditor({
  values = [],
  onChange,
  productId = null,
  persistOnSave = false,
}) {
  const [showModal, setShowModal] = useState(false);
  const [draftMap, setDraftMap] = useState(() => buildDraftFromValues(values));
  const [saving, setSaving] = useState(false);

  const savedValues = normalizeNutritionValuesForSave(values);

  useEffect(() => {
    if (!showModal) {
      setDraftMap(buildDraftFromValues(values));
    }
  }, [values, showModal]);

  const openModal = () => {
    setDraftMap(buildDraftFromValues(values));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setDraftMap(buildDraftFromValues(values));
  };

  const toggleDraftType = (type) => {
    setDraftMap((prev) => {
      const next = new Map(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.set(type, "");
      }
      return next;
    });
  };

  const updateDraftQuantity = (type, quantity) => {
    setDraftMap((prev) => {
      const next = new Map(prev);
      if (!next.has(type)) return next;
      next.set(type, quantity);
      return next;
    });
  };

  const persistValues = async (nextValues) => {
    if (!persistOnSave || !productId) return true;

    const token = localStorage.getItem("tokenLogin");
    await axios.put(
      `${config.dev}/v2/products/edit/${productId}`,
      { nutritionValues: nextValues },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return true;
  };

  const removeSavedItem = async (type) => {
    const nextValues = savedValues.filter((item) => item.type !== type);
    onChange(nextValues);

    if (persistOnSave && productId) {
      try {
        await persistValues(nextValues);
        toast.success("Nutrition value removed.");
      } catch (error) {
        console.error("Failed to remove nutrition value:", error);
        toast.error(
          error.response?.data?.message || "Failed to update nutrition values."
        );
      }
    }
  };

  const handleSave = async () => {
    const nextValues = normalizeNutritionValuesForSave(
      Array.from(draftMap.entries()).map(([type, value]) => ({ type, value }))
    );

    onChange(nextValues);

    if (persistOnSave && productId) {
      setSaving(true);
      try {
        await persistValues(nextValues);
        toast.success("Nutrition values saved. They are now visible to clients.");
      } catch (error) {
        console.error("Failed to save nutrition values:", error);
        toast.error(
          error.response?.data?.message || "Failed to save nutrition values."
        );
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    setShowModal(false);
  };

  const draftSelected = Array.from(draftMap.keys());

  return (
    <div className="prod-extra-editor nutrition-values-editor">
      <div className="prod-extra-card">
      <button type="button" className="prod-extra-trigger" onClick={openModal}>
        <span className="prod-extra-trigger__icon" aria-hidden>
          <i className="fas fa-leaf" />
        </span>
        <span className="prod-extra-trigger__copy">
          <span className="prod-extra-trigger__title">Nutrition</span>
          <span className="prod-extra-trigger__hint">Dietary labels shown to clients</span>
        </span>
        {savedValues.length > 0 ? (
          <span className="prod-extra-trigger__count">{savedValues.length}</span>
        ) : null}
        <i className="fas fa-chevron-right prod-extra-trigger__chevron" aria-hidden />
      </button>

      {savedValues.length > 0 && (
        <div className="nutrition-values-summary prod-extra-card__chips">
          {savedValues.map((item) => {
            const option = getNutritionOption(item.type);
            return (
              <span key={item.type} className="nutrition-values-summary-chip">
                <span className="nutrition-values-summary-chip__icon" aria-hidden="true">
                  {option.icon}
                </span>
                <span className="nutrition-values-summary-chip__text">
                  {option.label}
                  {item.value ? ` · ${item.value}` : ""}
                </span>
                <button
                  type="button"
                  className="nutrition-values-summary-chip__remove"
                  onClick={() => removeSavedItem(item.type)}
                  aria-label={`Remove ${option.label}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
      </div>

      <Modal show={showModal} onHide={closeModal} centered className="prod-extra-modal nutrition-values-modal">
        <Modal.Header closeButton>
          <Modal.Title>Nutrition values</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="nutrition-values-modal__intro">
            Select labels. Quantity is optional.
          </p>

          <div className="nutrition-values-picker-grid">
            {NUTRITION_OPTIONS.map((option) => {
              const isSelected = draftMap.has(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`nutrition-values-picker-card${isSelected ? " is-selected" : ""}`}
                  onClick={() => toggleDraftType(option.value)}
                >
                  <span className="nutrition-values-picker-card__icon" aria-hidden="true">
                    {option.icon}
                  </span>
                  <span className="nutrition-values-picker-card__label">{option.label}</span>
                </button>
              );
            })}
          </div>

          {draftSelected.length > 0 && (
            <div className="nutrition-values-quantity-list">
              <h6 className="nutrition-values-quantity-list__title">Optional quantity</h6>
              {draftSelected.map((type) => {
                const option = getNutritionOption(type);
                return (
                  <div key={type} className="nutrition-values-quantity-row">
                    <div className="nutrition-values-quantity-row__label">
                      <span aria-hidden="true">{option.icon}</span>
                      <span>{option.label}</span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={option.placeholder}
                      value={draftMap.get(type) || ""}
                      onChange={(e) => updateDraftQuantity(type, e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => toggleDraftType(type)}
                      aria-label={`Remove ${option.label}`}
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
